import {useState} from "react";
import {PhoneHQButton, PhoneHQTabs} from "ch3-ui-lib";
import {Link, useNavigate, useParams} from "react-router-dom";
import {EnsOverview} from "./Overview/EnsOverview";
import {EnsHistory} from "./History/EnsHistory";
import {EnsNotifications} from "./Notifications/EnsNotifications";
import {EnsSettings} from "./Settings/EnsSettings";
import {CreateAlertWizard} from "./CreateAlertWizard/CreateAlertWizard";
import {AlertDetailsPanel} from "./AlertDetailsPanel/AlertDetailsPanel";
import {useGetEnsStatsQuery} from "../../store/features/ens/ensService";
import {EnsAlertTemplate} from "../../store/features/ens/types";
import {EnsTemplates} from "./Templates/EnsTemplates";
import {Plus, Phone} from "lucide-react"
import {ENS_ALERT_REFRESH_INTERVAL_MS} from "./utils";

const PendingPill = ({count}: { count: number }) => (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-warning-line bg-warning-tint text-warning-ink text-[11px] font-bold">
        {count} pending
    </span>
);

const EnsMain = () => {
    const [wizardOpen, setWizardOpen] = useState(false);
    const [wizardTemplate, setWizardTemplate] = useState<EnsAlertTemplate | null>(null);
    const [activeTab, setActiveTab] = useState<string>('overview');
    const {publicId} = useParams<{ publicId?: string }>();
    const navigate = useNavigate();
    const {data: stats} = useGetEnsStatsQuery(
        {days: 30},
        {pollingInterval: ENS_ALERT_REFRESH_INTERVAL_MS},
    );
    const pendingCount = stats?.pendingCount ?? 0;

    const handleTabChange = (tabId: string) => {
        if (publicId) {
            navigate('/admin/ens');
        }
        setActiveTab(tabId);
    };

    const openBlankWizard = () => {
        setWizardTemplate(null);
        setWizardOpen(true);
    };

    const openTemplateWizard = (template: EnsAlertTemplate) => {
        setWizardTemplate(template);
        setWizardOpen(true);
    };

    const closeWizard = () => {
        setWizardOpen(false);
        setWizardTemplate(null);
    };

    const openTemplatesFromWizard = () => {
        closeWizard();
        if (publicId) {
            navigate('/admin/ens');
        }
        setActiveTab('templates');
    };

    const tabs = [
        {id: 'overview', label: 'Overview', content: <EnsOverview/>},
        {id: 'history', label: 'History', content: <EnsHistory/>},
        {id: 'notifications', label: 'Notification preview', content: <EnsNotifications/>},
        {id: 'templates', label: 'Templates', content: <EnsTemplates onUseTemplate={openTemplateWizard}/>},
        {id: 'settings', label: 'Settings', content: <EnsSettings/>},
    ];

    // The alert detail panel is a reserved right-hand column only on very wide
    // screens. Below Full HD it opens over the Overview list after selecting an alert.
    const effectiveTab = publicId ? 'overview' : activeTab;
    const showDetailPanel = effectiveTab === 'overview';

    return (
        <div className="flex h-full w-full min-w-0 flex-col overflow-hidden">
            <div className="flex min-h-[52px] shrink-0 flex-wrap items-center justify-between gap-2 border-b border-line px-3 py-2 sm:px-5">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <h2 className="truncate text-[15px] font-bold text-ink">Emergency Notification System</h2>
                    {pendingCount > 0 && <PendingPill count={pendingCount}/>}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <Link to="/admin/ens/contacts">
                        <PhoneHQButton
                            phq-id="ensMain-contactsBtn"
                            variant="secondary"
                            size="sm"
                        >
                            <Phone size={15} className={'!p-0'}/>
                            <span className="hidden sm:inline">User contacts</span>
                        </PhoneHQButton>
                    </Link>
                    <PhoneHQButton
                        phq-id="ensMain-createAlertBtn"
                        variant="primary"
                        size="sm"
                        onClick={openBlankWizard}
                    >
                        <Plus size={15} className={'!p-0'} color='white'/>
                        <span className="hidden sm:inline">New alert</span>
                    </PhoneHQButton>
                </div>
            </div>
            <div className="relative flex min-h-0 flex-1 overflow-hidden min-[1920px]:overflow-visible">
                <div className="min-w-0 flex-1 overflow-y-auto">
                    <PhoneHQTabs
                        variant="underline"
                        tabs={tabs}
                        activeTab={effectiveTab}
                        onTabChange={handleTabChange}
                        listClassName="px-5 border-line [&_[role=tab]]:px-[14px] [&_[role=tab]:first-child]:pl-0 [&_[role=tab]]:text-[13px] [&_[role=tab]]:font-semibold [&_[role=tab]:hover]:text-indigo-500"
                    />
                </div>
                {showDetailPanel && (
                    <AlertDetailsPanel
                        publicId={publicId ?? null}
                        onClose={() => navigate('/admin/ens')}
                    />
                )}
            </div>
            <CreateAlertWizard
                isOpen={wizardOpen}
                onClose={closeWizard}
                initialTemplate={wizardTemplate}
                onInitialTemplateConsumed={() => setWizardTemplate(null)}
                onManageTemplates={openTemplatesFromWizard}
            />
        </div>
    );
};

export default EnsMain;
